import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { UnifiedSearchResult } from './UnifiedSearchResult';
import { logger } from '../../../core';
import './SearchBar.css';

/**
 * @param {'unified' | 'qaListFilter'} [mode='unified'] — unified learn search API vs local Q&A list filter (Questions & Answers subpage).
 */
export function SearchBar({
  placeholder = 'Search tutorials, videos, Q&A, trivia...',
  mode = 'unified',
  searchTerm: controlledSearchTerm,
  onSearchChange
}) {
  const [internalSearchTerm, setInternalSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const navigate = useNavigate();

  const isQaListFilter = mode === 'qaListFilter';
  const searchTerm = isQaListFilter ? (controlledSearchTerm ?? '') : internalSearchTerm;

  const performUnifiedSearch = useCallback(async (query) => {
    if (!query || !query.trim()) {
      setSearchResults([]);
      setShowResults(false);
      return;
    }

    setIsSearching(true);
    try {
      const response = await fetch(`/api/learn/search?q=${encodeURIComponent(query.trim())}&limit=15`);
      const data = await response.json();

      if (data.success && data.results) {
        setSearchResults(data.results);
        setShowResults(data.results.length > 0);
      } else {
        setSearchResults([]);
        setShowResults(false);
      }
    } catch (error) {
      logger.error({
        tags: 'learn|search|error',
        color1: 'red',
      }, 'Unified search error:', error);
      setSearchResults([]);
      setShowResults(false);
    } finally {
      setIsSearching(false);
    }
  }, []);

  useEffect(() => {
    if (isQaListFilter) return undefined;
    const debounceTimer = setTimeout(() => {
      performUnifiedSearch(searchTerm);
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [searchTerm, performUnifiedSearch, isQaListFilter]);

  const handleSearchChange = (e) => {
    const v = e.target.value;
    if (isQaListFilter) {
      onSearchChange?.(v);
    } else {
      setInternalSearchTerm(v);
    }
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
  };

  const handleResultClick = (result) => {
    setShowResults(false);
    setSearchResults([]);

    if (result.type === 'qa') {
      navigate(`/learn/questions-answers?qa=${encodeURIComponent(result.id)}`);
    } else if (result.type === 'video') {
      navigate(`/learn/tutorial-videos?video=${result.id}&tab=${result.categoryId}`);
    } else if (result.type === 'trivia') {
      navigate('/learn/trivia');
    }
  };

  const handleSearchFocus = () => {
    if (!isQaListFilter && searchResults.length > 0) {
      setShowResults(true);
    }
  };

  const handleSearchBlur = () => {
    if (isQaListFilter) return;
    setTimeout(() => setShowResults(false), 200);
  };

  return (
    <div className="learn-search-section">
      <form className="learn-search-form" onSubmit={handleSearchSubmit}>
        <div className="learn-search-container">
          <i className="fa-solid fa-magnifying-glass learn-search-icon"></i>
          <input
            type="text"
            value={searchTerm}
            onChange={handleSearchChange}
            onFocus={handleSearchFocus}
            onBlur={handleSearchBlur}
            placeholder={placeholder}
            className="learn-search-input"
          />
          <button type="submit" className="learn-search-button">
            <i className="fa-solid fa-magnifying-glass"></i>
          </button>
        </div>
      </form>

      {!isQaListFilter && showResults && searchResults.length > 0 && (
        <div className="learn-search-results-dropdown">
          {searchResults.map((result) => (
            <UnifiedSearchResult
              key={`${result.type}-${result.id}`}
              result={result}
              onResultClick={handleResultClick}
            />
          ))}
        </div>
      )}

      {!isQaListFilter && showResults && searchResults.length === 0 && searchTerm.trim() && !isSearching && (
        <div className="learn-search-no-results">
          <i className="fa-solid fa-search"></i>
          <p>No results found for "{searchTerm}"</p>
          <small>Try different keywords</small>
        </div>
      )}
    </div>
  );
}
